import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, FileText, Database, HelpCircle, ChevronRight, Clock, CheckCircle2, Archive } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { CreateProjectDialog } from '@/components/CreateProjectDialog';
import { useQuery } from '@tanstack/react-query';
import { listProjects, archiveProject } from '@/lib/api/projects';
import { roleCanEdit, NO_PERMISSION_TOOLTIP } from '@/hooks/useProjectRole';
import type { ProjectMemberRole } from '@/types/backend';
import { formatDistanceToNow, isValid, parseISO } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface TeamMember {
  initials: string;
  email: string;
  color: string;
}

function deriveInitials(email: string): string {
  return email.split('@')[0].split('.').map(s => s[0]?.toUpperCase()).filter(Boolean).join('').slice(0, 2) || email.slice(0, 2).toUpperCase();
}

interface Project {
  id: string;
  name: string;
  client: string;
  dealType: string;
  createdAt: string;
  rfiStatus: { total: number; received: number };
  qaStatus: { total: number; answered: number };
  databookStatus: 'not_started' | 'in_progress' | 'complete';
  lastActivity: string;
  team: TeamMember[];
  currentUserRole: ProjectMemberRole;
}

const getDatabookStatusLabel = (status: Project['databookStatus']) => {
  switch (status) {
    case 'not_started': return 'Not Started';
    case 'in_progress': return 'In Progress';
    case 'complete': return 'Complete';
  }
};

const formatLastActivity = (iso: string) => {
  const d = parseISO(iso);
  if (!isValid(d)) return iso;
  return formatDistanceToNow(d, { addSuffix: true });
};

const Home = () => {
  const navigate = useNavigate();
  const [projectToArchive, setProjectToArchive] = useState<Project | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  
  const { data: backendProjects, isLoading, isError, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: () => listProjects(),
  });

  const TEAM_COLORS = ['bg-purple-100 text-purple-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700', 'bg-blue-100 text-blue-700', 'bg-rose-100 text-rose-700'];

  const projects: Project[] = (backendProjects ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    client: '',
    dealType: 'Buy-side FDD',
    createdAt: p.created_at,
    rfiStatus: { total: 24, received: 0 },
    qaStatus: { total: 0, answered: 0 },
    databookStatus: 'not_started' as const,
    lastActivity: formatLastActivity(p.updated_at),
    team: p.members.map((m, idx) => ({
      initials: deriveInitials(m.email),
      email: m.email,
      color: TEAM_COLORS[idx % TEAM_COLORS.length],
    })),
    currentUserRole: p.current_user_role,
  }));
  
  // Backend filters out archived projects, so we render all projects from the API
  const projectsToRender = projects;

  const handleNewProject = () => {
    setCreateProjectOpen(true);
  };

  const handleProjectCreated = () => {
    refetch();
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  // Onboarding — show once for new users
  const HOME_ONBOARD_TIPS = [
    { target: 'home-new-project', text: 'Welcome to Dilli! Start by creating a new project for your financial due diligence engagement.', pos: 'below' as const },
    { target: 'home-projects-area', text: 'Your projects will appear here. Click on a project to open it and begin the FDD workflow.', pos: 'below' as const },
    { target: 'home-sidebar-nav', text: 'Use the sidebar to navigate between your projects, archived deals, and account settings.', pos: 'right' as const },
  ];
  const [homeOnboardStep, setHomeOnboardStep] = useState(0);
  const [homeOnboardDismissed, setHomeOnboardDismissed] = useState(() => {
    return localStorage.getItem('dilli_home_onboarded') === 'true';
  });
  const [homeTipRect, setHomeTipRect] = useState<{ top: number; left: number; width: number; height: number; pos: string } | null>(null);
  const showHomeOnboard = !homeOnboardDismissed && !isLoading;

  useEffect(() => {
    if (homeOnboardDismissed || homeOnboardStep >= HOME_ONBOARD_TIPS.length) {
      setHomeTipRect(null);
      return;
    }
    const tip = HOME_ONBOARD_TIPS[homeOnboardStep];
    const measure = () => {
      const el = document.querySelector(`[data-onboard="${tip.target}"]`);
      if (!el) { setHomeTipRect(null); return; }
      const r = el.getBoundingClientRect();
      setHomeTipRect({ top: r.top, left: r.left, width: r.width, height: r.height, pos: tip.pos });
    };
    const t = setTimeout(measure, 600);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [homeOnboardStep, homeOnboardDismissed, isLoading]);

  const advanceOnboard = () => {
    if (homeOnboardStep < HOME_ONBOARD_TIPS.length - 1) {
      setHomeOnboardStep(s => s + 1);
      setHomeTipRect(null);
    } else {
      setHomeOnboardDismissed(true);
      localStorage.setItem('dilli_home_onboarded', 'true');
    }
  };

  const dismissOnboard = () => {
    setHomeOnboardDismissed(true);
    localStorage.setItem('dilli_home_onboarded', 'true');
  };

  const handleArchiveProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectToArchive(project);
  };

  const confirmArchive = async () => {
    if (!projectToArchive) return;

    setIsArchiving(true);
    try {
      await archiveProject(projectToArchive.id);
      setProjectToArchive(null);
      // Refetch projects list to reflect the change (archived projects won't appear)
      refetch();
    } catch (error: any) {
      console.error('Failed to archive project:', error);
      const errorMessage = error?.message || 'Failed to archive project. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader showBack={false} />

      <main className="container mx-auto px-8 py-10">
        <div className="max-w-5xl mx-auto">
          {/* Page header */}
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1 className="text-2xl font-semibold text-foreground tracking-tight mb-1">
                Projects
              </h1>
              {isError && (
                <p className="text-xs text-muted-foreground mt-1">
                  Failed to load projects.
                </p>
              )}
            </div>
          </div>

          {/* Projects list */}
          <div data-onboard="home-projects-area" className="space-y-4">
            {projectsToRender.map((project) => {
              return (
                <div
                  key={project.id}
                  className="border border-border/50 rounded-xl bg-card hover:border-border hover:shadow-sm transition-all cursor-pointer group p-5"
                  onClick={() => handleProjectClick(project.id)}
                >
                  {/* Top row - Name, time, archive */}
                  <div className="flex items-start justify-between gap-4 mb-1">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground tracking-tight">
                        {project.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">{project.dealType}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{project.lastActivity}</span>
                      </div>
                      {roleCanEdit(project.currentUserRole) && (
                        <button
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100 px-2 py-1 rounded hover:bg-muted"
                          onClick={(e) => handleArchiveProject(project, e)}
                        >
                          <Archive className="w-3.5 h-3.5" />
                          Archive
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Bottom row - Stats + Team avatars */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-5">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/30 border border-border/30">
                        <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                        <div>
                          <span className="text-xs text-muted-foreground">RFI</span>
                          <span className="text-sm font-semibold text-foreground ml-1.5">
                            {project.rfiStatus.received}/{project.rfiStatus.total}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/30 border border-border/30">
                        <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" />
                        <div>
                          <span className="text-xs text-muted-foreground">Q&A</span>
                          <span className="text-sm font-semibold text-foreground ml-1.5">
                            {project.qaStatus.answered}/{project.qaStatus.total}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/30 border border-border/30">
                        <Database className="w-3.5 h-3.5 text-muted-foreground" />
                        <div>
                          <span className="text-xs text-muted-foreground">Databook</span>
                          <span className="text-sm font-semibold text-foreground ml-1.5">
                            {getDatabookStatusLabel(project.databookStatus)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Team avatars */}
                    <div className="flex items-center -space-x-2">
                      {project.team.map((member) => (
                        <div
                          key={member.email}
                          className={`w-8 h-8 rounded-full ${member.color} flex items-center justify-center text-xs font-semibold border-2 border-card`}
                          title={member.email}
                        >
                          {member.initials}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty state (hidden when projects exist) */}
          {!isLoading && projectsToRender.length === 0 && (
            <Card className="border-border/50 border-dashed">
              <CardContent className="py-16 text-center">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full border border-border/40 bg-muted/30 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-foreground/40" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 tracking-tight">No projects yet</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Get started by creating your first project
                </p>
                <Button
                  data-onboard="home-new-project"
                  onClick={handleNewProject}
                  className="gap-2 bg-foreground text-background hover:bg-foreground/90"
                >
                  <Plus className="w-4 h-4" />
                  Create Project
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={createProjectOpen}
        onOpenChange={setCreateProjectOpen}
        onSuccess={handleProjectCreated}
      />

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={!!projectToArchive} onOpenChange={() => setProjectToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive "{projectToArchive?.name}"? You can restore it later from the archived projects.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmArchive} disabled={isArchiving}>
              {isArchiving ? 'Archiving...' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Onboarding Overlay */}
      {showHomeOnboard && homeTipRect && (
        <>
          <div onClick={advanceOnboard} style={{ position: 'fixed', inset: 0, zIndex: 9990, cursor: 'pointer' }} />
          <div style={{
            position: 'fixed',
            top: homeTipRect.top - 8,
            left: homeTipRect.left - 8,
            width: homeTipRect.width + 16,
            height: homeTipRect.height + 16,
            borderRadius: 10,
            boxShadow: '0 0 0 9999px rgba(26,35,50,0.45)',
            transition: 'all .35s ease',
            zIndex: 9991,
            pointerEvents: 'none',
          }} />
          {(() => {
            const tip = HOME_ONBOARD_TIPS[homeOnboardStep];
            let tipTop = homeTipRect.top + homeTipRect.height + 18;
            let tipLeft = Math.max(16, homeTipRect.left);
            if (tip.pos === 'right') {
              tipTop = Math.max(80, homeTipRect.top);
              tipLeft = homeTipRect.left + homeTipRect.width + 18;
            } else if (tip.pos === 'left') {
              tipTop = Math.max(80, homeTipRect.top);
              tipLeft = Math.max(16, homeTipRect.left - 298);
            }
            return (
              <div style={{
                position: 'fixed', top: tipTop, left: tipLeft,
                background: '#fff', borderRadius: 14, padding: '18px 22px',
                width: 280, boxShadow: '0 8px 30px rgba(0,0,0,.18)', zIndex: 9999,
              }}>
                <p style={{ fontSize: 13, lineHeight: 1.6, color: '#1a1a1a', margin: 0 }}>
                  {tip.text}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
                  <span style={{ fontSize: 11, color: '#999' }}>
                    {homeOnboardStep + 1} of {HOME_ONBOARD_TIPS.length}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={(e) => { e.stopPropagation(); dismissOnboard(); }}
                      style={{ fontSize: 12, color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>
                      Skip
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); advanceOnboard(); }}
                      style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: '#1a1a1a', border: 'none', borderRadius: 8, padding: '6px 16px', cursor: 'pointer' }}>
                      {homeOnboardStep < HOME_ONBOARD_TIPS.length - 1 ? 'Next' : 'Got it'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default Home;
